import { useState } from "react";
import { Card, CardContent, Label } from "@learn-me/ui";
import { useCourseWizard } from "@/hooks";
import { FileUploader } from "../FileUploader";
import type { TempFile } from "../../api/adminApi";
import { FileText, MessageSquareText, Info, Sparkles } from "lucide-react";

export function WizardStep2References() {
  const {
    referenceFiles,
    addReferenceFile,
    removeReferenceFile,
    referenceText,
    setReferenceText,
  } = useCourseWizard();

  // Convert wizard files to TempFile format for FileUploader
  const tempFiles: TempFile[] = referenceFiles.map((f) => ({
    publicId: f.id,
    url: f.url,
    name: f.name,
    type: (f.type.includes("pdf") ? "PDF" : "IMAGE") as "PDF" | "IMAGE",
    mimeType: f.type,
    size: 0,
  }));

  const handleFilesChange = (files: TempFile[]) => {
    // Find removed files
    const currentIds = new Set(referenceFiles.map((f) => f.id));
    const newIds = new Set(files.map((f) => f.publicId));

    // Remove files that are no longer in the list
    referenceFiles.forEach((f) => {
      if (!newIds.has(f.id)) {
        removeReferenceFile(f.id);
      }
    });

    // Add new files
    files.forEach((f) => {
      if (!currentIds.has(f.publicId)) {
        addReferenceFile({
          id: f.publicId,
          name: f.name,
          type: f.type,
          url: f.url,
        });
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-medium">
            Este paso es opcional pero recomendado
          </p>
          <p className="text-sm text-blue-700 mt-1">
            Agregar documentos de referencia ayuda a la AI a generar contenido
            mas especifico y relevante para tu contexto. Sin referencias, el
            contenido sera mas generico.
          </p>
        </div>
      </div>

      {/* File upload */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <FileText className="h-5 w-5 text-primary-600" />
              </div>
              <div className="flex-1">
                <Label className="text-base font-medium">
                  Documentos de referencia
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Sube PDFs, presentaciones o imagenes que contengan el material
                  base para tu curso. La AI extraera y analizara el contenido.
                </p>
              </div>
            </div>

            <FileUploader
              files={tempFiles}
              onFilesChange={handleFilesChange}
              maxFiles={10}
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            />
          </div>
        </CardContent>
      </Card>

      {/* Text reference */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquareText className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <Label
                  htmlFor="referenceText"
                  className="text-base font-medium"
                >
                  Texto o notas adicionales
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Puedes pegar texto de referencia, URLs de articulos, o notas
                  que quieras que la AI considere al crear el contenido.
                </p>
              </div>
            </div>

            <textarea
              id="referenceText"
              value={referenceText}
              onChange={(e) => setReferenceText(e.target.value)}
              placeholder="Pega aqui cualquier texto de referencia, URLs de articulos, o notas relevantes para el curso..."
              className="w-full min-h-[150px] p-3 border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              rows={6}
            />

            {referenceText.length > 0 && (
              <p className="text-xs text-gray-500 text-right">
                {referenceText.length.toLocaleString()} caracteres
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI hint */}
      <div className="bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-lg p-4 flex gap-3">
        <Sparkles className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-primary-800 font-medium">
            Consejo para mejores resultados
          </p>
          <p className="text-sm text-primary-700 mt-1">
            Mientras mas contexto proporciones, mas personalizado sera el curso.
            Incluye ejemplos especificos de tu industria, casos de estudio, o
            terminologia particular que quieras usar.
          </p>
        </div>
      </div>
    </div>
  );
}
